import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { ChevronDownIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import ClickAwayListener from '../../components/ClickAwayListener';

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  className = '',
  isDisabled = false,
  components = {},
  styles = {},
  menuPlacement = 'bottom',
  menuPortalTarget = null,
  autoFocus = false,
  openMenuOnFocus = false,
  inputValue = '',
  onInputChange = () => {},
  isMulti = true,
  isClearable = true,
  isSearchable = true,
  allowCreation = false,
  noOptionsMessage = () => 'No options available',
  onKeyDown,
  onMenuOpen,
  onMenuClose,
  menuIsOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(menuIsOpen);
  const [searchTerm, setSearchTerm] = useState(inputValue);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  // Handle outside clicks
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setIsOpen(menuIsOpen);
  }, [menuIsOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(
    (option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()) && 
    !value.some(v => v.value === option.value)
  );

  // Handle option selection
  const handleSelect = (option) => {
    const newValue = [...value, option];
    onChange(newValue);
    setSearchTerm('');
    onInputChange('');
    if (onMenuClose) onMenuClose();
  };

  // Handle option removal
  const handleRemove = (optionToRemove) => {
    const newValue = value.filter(option => option.value !== optionToRemove.value);
    onChange(newValue);
  };

  // Handle clear all
  const handleClear = () => {
    onChange([]);
  };

  // Handle create option
  const handleCreate = () => {
    if (!searchTerm) return;
    
    const newOption = { value: searchTerm, label: searchTerm };
    handleSelect(newOption);
  };

  // Calculate menu position
  const getMenuPosition = () => {
    if (!containerRef.current) return {};
    
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (menuPlacement === 'auto') {
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        return { bottom: '100%', maxHeight: `${spaceAbove - 10}px` };
      } else {
        return { top: '100%', maxHeight: `${spaceBelow - 10}px` };
      }
    } else if (menuPlacement === 'top') {
      return { bottom: '100%', maxHeight: `${spaceAbove - 10}px` };
    } else {
      return { top: '100%', maxHeight: `${spaceBelow - 10}px` };
    }
  };

  // Custom components
  const DropdownIndicator = components.DropdownIndicator || (() => (
    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
  ));

  const Menu = components.Menu || (({ children }) => (
    <div 
      ref={menuRef}
      className="absolute z-50 w-full bg-white shadow-lg rounded-md py-1 mt-1 overflow-auto"
      style={getMenuPosition()}
    >
      {children}
    </div>
  ));

  const handleInputKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (e.key === 'Enter' && allowCreation && searchTerm) {
      handleCreate();
      e.preventDefault();
    }
  };

  const handleToggleMenu = () => {
    if (isDisabled) return;
    
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    if (newIsOpen && onMenuOpen) {
      onMenuOpen();
    } else if (!newIsOpen && onMenuClose) {
      onMenuClose();
    }
  };

  return (
    <ClickAwayListener onClickAway={() => {
      setIsOpen(false);
      if (onMenuClose) onMenuClose();
    }}>
      <div 
        ref={containerRef}
        className={`relative ${className}`}
      >
        <div 
          className={`flex flex-wrap items-center gap-1 w-full px-2 py-1 border border-gray-300 rounded-md bg-white min-h-[38px] ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text'}`}
          onClick={() => {
            if (!isDisabled) {
              handleToggleMenu();
              inputRef.current?.focus();
            }
          }}
          style={styles.control}
        >
          {value.map((option) => (
            <div 
              key={option.value}
              className={`flex items-center bg-blue-100 text-blue-800 rounded-md px-2 py-1 text-sm ${isDisabled ? 'opacity-60' : ''}`}
              style={option.color ? { backgroundColor: option.color, color: getLuminance(option.color) > 0.5 ? 'black' : 'white' } : {}}
            >
              <span>{option.label}</span>
              {!isDisabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option);
                  }}
                  className="ml-1 text-current hover:text-current focus:outline-none"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          
          {!isDisabled && (
            <input
              ref={inputRef}
              type="text"
              className="flex-grow outline-none bg-transparent min-w-[50px] text-sm"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onInputChange(e.target.value);
              }}
              onKeyDown={handleInputKeyDown}
              placeholder={value.length === 0 ? placeholder : ""}
              disabled={isDisabled}
            />
          )}
          
          {isClearable && value.length > 0 && !isDisabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          
          {!isDisabled && (
            <DropdownIndicator />
          )}
        </div>
        
        {isOpen && !isDisabled && (
          menuPortalTarget ? (
            ReactDOM.createPortal(
              <Menu>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                      onClick={() => handleSelect(option)}
                      style={styles.option}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  allowCreation && searchTerm ? (
                    <div 
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center text-blue-600"
                      onClick={handleCreate}
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Create "{searchTerm}"
                    </div>
                  ) : (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      {typeof noOptionsMessage === 'function' ? noOptionsMessage({ inputValue: searchTerm }) : noOptionsMessage}
                    </div>
                  )
                )}
              </Menu>,
              menuPortalTarget
            )
          ) : (
            <Menu>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => handleSelect(option)}
                    style={styles.option}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                allowCreation && searchTerm ? (
                  <div 
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center text-blue-600"
                    onClick={handleCreate}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Create "{searchTerm}"
                  </div>
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    {typeof noOptionsMessage === 'function' ? noOptionsMessage({ inputValue: searchTerm }) : noOptionsMessage}
                  </div>
                )
              )}
            </Menu>
          )
        )}
      </div>
    </ClickAwayListener>
  );
};

// Helper function to calculate luminance of a color
const getLuminance = (color) => {
  // Default to 0.5 if color is not provided or invalid
  if (!color || typeof color !== 'string') return 0.5;
  
  // Convert hex to RGB
  let r, g, b;
  if (color.startsWith('#')) {
    const hex = color.substring(1);
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  } else if (color.startsWith('rgb')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (!match) return 0.5;
    r = parseInt(match[1], 10) / 255;
    g = parseInt(match[2], 10) / 255;
    b = parseInt(match[3], 10) / 255;
  } else {
    return 0.5;
  }
  
  // Calculate luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance;
};

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
    })
  ),
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  components: PropTypes.object,
  styles: PropTypes.object,
  menuPlacement: PropTypes.oneOf(['bottom', 'top', 'auto']),
  menuPortalTarget: PropTypes.any,
  autoFocus: PropTypes.bool,
  openMenuOnFocus: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  allowCreation: PropTypes.bool,
  noOptionsMessage: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  onKeyDown: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  menuIsOpen: PropTypes.bool,
};

export default MultiSelect;
